// Dev-only platform-admin fixtures (`admin.developerz.ai` / `?host=admin`),
// lifted out of `mocks.ts` with the six data literals they are made of. Pure
// DATA plus one dispatcher: nothing here decides anything the console does not
// already ask the server for.
//
// The goal board and the hive band moved to `admin-goals-mocks.ts` and
// `admin-hive-mocks.ts`: each is its own `/v1` family, and the goal board is the
// one fixture here that a write MUTATES.
//
// Entitlement grants and the cross-tenant run lookup are the SIBLING admin
// surfaces and live in `admin-grants-mocks.ts`: they are a write plane over a
// different scope, and they arrived as a closed gap rather than as a move. The
// access funnel (access requests + invitations) is `admin-access-mocks.ts`.
//
// STATEFUL since the user registry's approve / suspend became REST twins
// (`POST /v1/admin/users/:id/{approve,suspend}`, arc 101 slice 01): the row
// re-reads after the write, so a stateless echo would show a suspended person
// still approved on the next GET. `resetAdminConsoleMocks` is the reset seam.

import type {
  AdminAccountRow,
  AdminAuditRow,
  AdminOverview,
  AdminUserDetail,
  AdminUserRow,
  Page,
} from './admin';
import { withTrends } from './admin-trend-mocks';
import type { AdminGrant } from './adminGrants';
import { at } from './mock-session';
import { unhandledMockRoute } from './unhandled-mock-route';

// Platform-admin radar (admin.developerz.ai / ?host=admin). Sample counters +
// registries so the admin screens render populated in mock mode.
const adminOverview: Omit<AdminOverview, 'trends'> = {
  accounts: { total: 128, by_tier: { free: 92, pro: 31, enterprise: 5 } },
  users: { total: 342, approved: 300, waitlisted: 42 },
  runs: { total: 18420, last_24h: 214 },
  fleet: { total: 24, online: 18, offline: 4, pending: 1, revoked: 1 },
  queue: { depth: 12, pending: 5, assigned: 3, in_progress: 4, failed: 0 },
  spend: { mtd_usd: '4210.55' },
  billing: {
    paying_accounts: 31,
    paid_seats: 214,
    purchased_ci_slots: 12,
    paused_accounts: 2,
    comped_ai_devs: 18,
    comped_accounts: 7,
  },
  reliability: {
    windowDays: 14,
    sampleSize: 903,
    escalationRate: 0.3178,
    cleanTerminalRate: 0.6357,
    failedRate: 0.0222,
    byDay: [
      { day: '2026-07-25', total: 140, completed: 90, escalated: 42, failed: 8 },
      { day: '2026-07-24', total: 132, completed: 84, escalated: 41, failed: 7 },
      { day: '2026-07-23', total: 121, completed: 78, escalated: 38, failed: 5 },
    ],
    byModel: [
      { model: 'claude-opus-4', total: 512, completed: 340, escalated: 150, failed: 22 },
      { model: 'gpt-4o', total: 391, completed: 234, escalated: 137, failed: 20 },
    ],
    escalationCauses: [
      { category: 'low_confidence', n: 121 },
      { category: 'ci_timeout', n: 88 },
      { category: 'provider_failure', n: 78 },
    ],
    // NOT GA-ready: clean 0.6357 < 0.9 (failed + sample + models all clear).
    gaGate: {
      pass: false,
      failures: [{ code: 'clean_terminal_below', observed: 0.6357, required: 0.9 }],
    },
  },
};

const adminAudit: readonly AdminAuditRow[] = [
  {
    id: 'ev_1',
    ts_iso: at('2026-07-20'),
    kind: 'session.completed',
    tool: 'triage',
    outcome: 'success',
    session_id: 'sess_a2',
    account_login: 'developerz-ai',
  },
  {
    id: 'ev_2',
    ts_iso: at('2026-07-20'),
    kind: 'pr.merged',
    tool: null,
    outcome: 'success',
    session_id: null,
    account_login: 'acme-corp',
  },
  {
    id: 'ev_3',
    ts_iso: at('2026-07-19'),
    kind: 'key.verify',
    tool: 'byok',
    outcome: 'failure',
    session_id: null,
    account_login: 'meepcastana',
  },
];

const adminAccounts: Page<AdminAccountRow> = {
  data: [
    {
      id: 'acc_devsample',
      github_login: 'developerz-ai',
      kind: 'org',
      tier: 'enterprise',
      seats: 5,
      spend_cap_usd: '1000.00',
      billing_paused_at_iso: null,
      oss_verified: true,
      created_at_iso: at('2026-01-12'),
      members_count: 3,
    },
    {
      id: 'acc_acme',
      github_login: 'acme-corp',
      kind: 'org',
      tier: 'pro',
      seats: 3,
      spend_cap_usd: '250.00',
      billing_paused_at_iso: null,
      oss_verified: false,
      created_at_iso: at('2026-03-01'),
      members_count: 8,
    },
    {
      id: 'acc_meep',
      github_login: 'meepcastana',
      kind: 'user',
      tier: 'free',
      seats: 1,
      spend_cap_usd: null, // no cap set: every prod account at 2026-10-01 (slice 17 M2)
      billing_paused_at_iso: at('2026-07-05'),
      oss_verified: true,
      created_at_iso: at('2026-05-18'),
      members_count: 1,
    },
  ],
  next_cursor: null,
};

const adminUserRows: AdminUserRow[] = [
  {
    id: 'usr_dev',
    login: 'din',
    email: 'din@developerz.ai',
    avatar: null,
    approved_at_iso: at('2026-01-12'),
    created_at_iso: at('2026-01-12'),
    memberships: [{ account_id: 'acc_devsample', github_login: 'developerz-ai', role: 'owner' }],
  },
  {
    id: 'usr_ada',
    login: 'ada-lovelace',
    email: 'ada@developerz.ai',
    avatar: 'https://avatars.githubusercontent.com/u/1?v=4',
    approved_at_iso: at('2026-02-02'),
    created_at_iso: at('2026-02-01'),
    memberships: [{ account_id: 'acc_devsample', github_login: 'developerz-ai', role: 'admin' }],
  },
  {
    id: 'usr_wait',
    login: 'newcomer',
    email: null,
    avatar: null,
    approved_at_iso: null,
    created_at_iso: at('2026-07-10'),
    memberships: [],
  },
];

const adminUsers = (): Page<AdminUserRow> => ({ data: adminUserRows, next_cursor: null });

/** One grant whose subject is `usr_ada`, so her page paints a grants list. */
const adaGrant: AdminGrant = {
  id: 'grn_mock05',
  subject_kind: 'user',
  subject_id: 'usr_ada',
  kind: 'concurrency',
  amount: 2,
  source: 'operator',
  granted_by_user_id: 'usr_staff01',
  reason: 'Reviewing the docs migration',
  active: true,
  created_iso: at('2026-07-04'),
  expires_iso: null,
  revoked_iso: null,
  extends_to_owned_orgs: false,
};

/**
 * `GET /v1/admin/users/:id` (#5727), DERIVED from the registry row so an
 * approve / suspend shows on the person's page too. Membership `kind` is the
 * account fixture's own.
 */
function userDetail(row: AdminUserRow): AdminUserDetail {
  return {
    ...row,
    last_active_iso: row.approved_at_iso === null ? null : at('2026-07-21'),
    memberships: row.memberships.map((m) => ({
      ...m,
      kind: adminAccounts.data.find((a) => a.id === m.account_id)?.kind ?? 'org',
    })),
    grants: row.id === adaGrant.subject_id ? [adaGrant] : [],
  };
}

/** Approve stamps `approved_at`; suspend clears it. Idempotent, as the routes are. */
function decideUser(id: string, verb: string): Promise<unknown> {
  const i = adminUserRows.findIndex((u) => u.id === id);
  const row = adminUserRows[i];
  if (row === undefined) return unhandledMockRoute('POST', id);
  if (verb === 'approve') {
    const stamped: AdminUserRow = {
      ...row,
      approved_at_iso: row.approved_at_iso ?? at('2026-07-22'),
    };
    adminUserRows[i] = stamped;
    // Plan 102 slice 05: approving grants the bundle, and the 200 says what it holds
    // and the box budget that implies (a fixture number, 5 AI devs → 15).
    const grant = { grant_id: `grt_mock_${id}`, kind: 'ai_devs', amount: 5, expires_at_iso: null };
    return Promise.resolve({ kind: 'approved', user_id: id, grants: [grant], boxes: 15 });
  } else if (verb === 'suspend') {
    const capped: AdminUserRow = { ...row, approved_at_iso: null };
    adminUserRows[i] = capped;
  } else {
    return unhandledMockRoute('POST', `/v1/admin/users/${id}/${verb}`);
  }
  return Promise.resolve({});
}

/**
 * Handle a `/v1/admin/*` radar request, or return `null` if `path` is not one
 * (so `mockFetch` falls through — `/v1/admin/goals`, `/v1/admin/hive`,
 * `/v1/admin/grants`, `/v1/admin/fleet/runs/*` and `/v1/admin/oss-verifications`
 * are served by their own modules).
 */
export function adminConsoleMock(path: string, method: string): Promise<unknown> | null {
  const bare = path.split('?')[0] ?? path;
  if (!bare.startsWith('/v1/admin/')) return null;

  // `POST /v1/admin/users/:id/{approve,suspend}` — the registry's write twins.
  if (method === 'POST' && bare.startsWith('/v1/admin/users/')) {
    const [id = '', verb = ''] = bare.slice('/v1/admin/users/'.length).split('/');
    return decideUser(decodeURIComponent(id), verb);
  }

  if (bare === '/v1/admin/overview') return Promise.resolve(withTrends(adminOverview, Date.now()));
  // The tail PAGES, and this fixture answers the envelope the client reads. It
  // used to reply the bare row ARRAY while `useAdminAudit` unwrapped `.data`, so
  // the panel error-stated on every mock render — the exact "stale fixture, green
  // suite" failure the envelope sweep exists to end.
  if (bare === '/v1/admin/audit') return Promise.resolve({ data: adminAudit, next_cursor: null });
  if (bare === '/v1/admin/accounts') return Promise.resolve(adminAccounts);
  if (bare.startsWith('/v1/admin/accounts/')) {
    const id = decodeURIComponent(bare.slice('/v1/admin/accounts/'.length));
    const row = adminAccounts.data.find((a) => a.id === id) ?? adminAccounts.data[0];
    return Promise.resolve({
      ...row,
      stripe_seat_subscription_id: null,
      members: [
        { user_id: 'usr_dev', login: 'din', avatar: null, role: 'owner' },
        {
          user_id: 'usr_ada',
          login: 'ada-lovelace',
          avatar: 'https://avatars.githubusercontent.com/u/1?v=4',
          role: 'admin',
        },
      ],
    });
  }
  if (bare === '/v1/admin/users') return Promise.resolve(adminUsers());
  if (bare.startsWith('/v1/admin/users/')) {
    const id = decodeURIComponent(bare.slice('/v1/admin/users/'.length));
    const row = adminUserRows.find((u) => u.id === id);
    return row === undefined ? unhandledMockRoute(method, path) : Promise.resolve(userDetail(row));
  }
  return null;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_USERS = adminUserRows.map((row) => structuredClone(row));

export function resetAdminConsoleMocks(): void {
  adminUserRows.splice(0, adminUserRows.length, ...PRISTINE_USERS.map((r) => structuredClone(r)));
}
